import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Typography, Switch } from '@mui/material';
import { styled } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import WidgetsIcon from '@mui/icons-material/Widgets';
import RateReviewIcon from '@mui/icons-material/RateReview';
import PeopleIcon from '@mui/icons-material/People';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { useCombinedContext } from '../AnimationContext';

const SidebarContainer = styled(Box)(({ theme }) => ({
  width: 240,
  backgroundColor: theme.palette.background.paper,
  height: '100vh',
  padding: theme.spacing(2),
}));

const Logo = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: '1.5rem',
  marginBottom: theme.spacing(4),
}));

const StyledListItemButton = styled(ListItemButton)<{ active: boolean }>(({ theme, active }) => ({
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1),
  backgroundColor: active ? theme.palette.action.selected : 'transparent',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const ThemeToggle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
}));

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/', created: true },
  { text: 'Widget', icon: <WidgetsIcon />, path: '/widget', created: true },
  { text: 'Reviews', icon: <RateReviewIcon />, path: '/review', created: true },
  { text: 'Customers', icon: <PeopleIcon />, path: '/customers', created: true },
  { text: 'Online Analysis', icon: <AssessmentIcon />, path: '/analysis', created: false },
  { text: 'Settings', icon: <SettingsIcon />, path: '/settings', created: true },
];

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useCombinedContext();

  const handleNavigation = (path: string, created: boolean) => {
    if (created) {
      navigate(path);
    }
  };

  return (
    <SidebarContainer>
      <Logo variant="h6">HIGHLEVELS</Logo>
      <List>
        {menuItems.map((item, index) => (
          <StyledListItemButton
            key={index}
            onClick={() => handleNavigation(item.path, item.created)}
            active={location.pathname === item.path}
            sx={{ opacity: item.created ? 1 : 0.5 }}
            disabled={!item.created}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </StyledListItemButton>
        ))}
      </List>
      <ThemeToggle>
        <Brightness7Icon />
        <Switch
          checked={isDarkMode}
          onChange={toggleTheme}
          color="default"
        />
        <Brightness4Icon />
      </ThemeToggle>
    </SidebarContainer>
  );
};

export default Sidebar;